/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import "@material/mwc-list/mwc-list";
import "@material/mwc-list/mwc-check-list-item";
import "@material/mwc-button";
import "@material/mwc-textfield";
import "@material/mwc-linear-progress";
import "@material/mwc-select";

import { css, html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators";
import { Consultant } from "../data-classes/consultant";
import { Client } from "../data-classes/client";
import { HitlElement } from "../hitl-element";
import { ConsultantClient } from "../data-classes/consultant-client";

/**
 * @class HitlUpload - Component for upload documents by selecting consultant and client
 */
@customElement("hitl-upload")
export class HitlUpload extends HitlElement {
  
  /**
   * Variable for token client
   */
  @property()
  tokenClient?: any;

  /**
   * client server id
   */
  clientId = process.env.CLIENT_ID;
  
  /**
   * Upload bucket
   */
  upload_bucket = process.env.UPLOAD_BUCKET;

  /**
   * Processorname
   */
  processorName = process.env.DEFAULT_PROCESSOR_NAME;

  /**
   * Processortype
   */
  processorType = process.env.DEFAULT_PROCESSOR_TYPE;

  /**
   * gis load promise
   */
  gisLoadPromise: Promise<void>;

  /**
   * gis load okay
   */
  gisLoadOkay: (() => void) | undefined;

  /**
   * gis load fail
   */
  gisLoadFail: ((error: any) => void) | undefined;

  /**
   * Files
   */
  files: File[] = [];

  /**
   * dialog text
   */
  dialogText = document.createElement('p');

  /**
   * Footer button ok
   */
  footerButtonOk = document.createElement('mwc-button');

  /**
   * Data transfer
   */
  dt: DataTransfer | null = null;

  /**
   * Button to upload documents
   */
  @property({type: Object})
  uploadButton: HTMLButtonElement | null = null;

  /**
   * droparea for documents
   */
  dropArea!: HTMLElement;

  /**
   * upload progress
   */
  uploadProgress: number[] = [];

  /**
   * progressbar
   */
  @property({type: Object})
  progressBar: HTMLProgressElement | null = null;

  static override styles = css`
    a {
      color: #369;
    }
    
    .mdc-select {
      width: 300%;
      min-width: fit-content;
    }

    .mdc-select--filled .mdc-select__anchor {
      width: fit-content;
    }

    mdc-menu-surface
      mdc-menu-surface--fullwidth
      mdc-menu-surface--is-open-below
      mdc-menu-surface--open {
      width: fit-content;
    }

    #drop-area {
      border: 2px dashed #ccc;
      border-radius: 20px;
      width: 80vw;
      height: 40vh;
      margin: 50px auto;
      padding: 20px;
    }

    #drop-area.highlight {
      border-color: white;
      background-color: rgb(36,76,140);
    }

    p {
      margin-top: 0;
    }

    .my-form {
      margin-bottom: 10px;
    }

    .button {
      font-size: 1.2em;
      display: inline-block;
      padding: 10px;
      background: #ccc;
      cursor: pointer;
      border-radius: 5px;
      border: 1px solid #ccc;
    }

    #fileElem {
      display: none;
    }

    #progress-bar {
      width: 100%;
      height: 5vh;
    }

    progress[value] {
        --color: rgb(36, 76, 140); 
        --background: #cccccc;
      
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        border: none;
        width: 200px;
        margin: 0 10px;
        border-radius: 10em;
        background: var(--background);
      }

      progress[value]::-webkit-progress-bar {
        border-radius: 10em;
        background: var(--background);
      }

      progress[value]::-webkit-progress-value {
        border-radius: 10em;
        background: var(--color);
      }

      progress[value]::-moz-progress-bar {
        border-radius: 10em;
        background: var(--color);
      }

      form {
        margin-top: 50px;
        margin-left: 10%;
      }

      span {
        width: 200px;
        display: inline-block;
      }

      input[type=button]:label {
          background-color: rgb(36,76,140);
      }

      .button {
          color: #ffffff;
          background-color: rgb(36,76,140);
      }
  `;
  
  /**
   * @constructor
   */
  constructor() {
    super();
    this.gisLoadPromise = new Promise((resolve, reject) => {
      this.gisLoadOkay = resolve;
      this.gisLoadFail = reject;
      this.handleDrop = this.handleDrop.bind(this);
    });
  }

  override async firstUpdated(){
    this.dropArea = this.shadowRoot?.getElementById('drop-area') as HTMLElement;
    this.dialog!.addEventListener("click", (event) => {
        const rect = this.dialog!.getBoundingClientRect();
        const isInDialog =
          rect.top <= event.clientY &&
          event.clientY <= rect.top + rect.height &&
          rect.left <= event.clientX &&
          event.clientX <= rect.left + rect.width;
        if (!isInDialog) {
          this.dialog!.open = false;
        }
    });
    
    //Initialization for consultants, clients, filterclients, filterconsultants
    if(this.database){
      const accessToken = await this.getAccessToken();        
      this.consultants = await Consultant.list(this.database, accessToken);
      this.filterConsultants = this.consultants;
      this.clients = await Client.list(this.database, accessToken);
      this.filterClients = this.clients;
      console.log("Initialization: ", this.filterClients);
      this.consultantClients = await ConsultantClient.list(this.database, accessToken);
    }
  }

  override async connectedCallback() {
    super.connectedCallback();
    await this.gisLoadPromise;
  }

  override async disconnectedCallback() {
    super.disconnectedCallback();
  }

  override render() {
    const template: TemplateResult[] = [];
    console.log("Render filter clients", this.filterClients);
    template.push(html`
      <div>
        <form>
          <label for="consultantId">
            <span><b>Berater:</b></span>
            <mwc-textfield 
                outlined
                label="Suche Berater.." TextFieldType="search" id="searchfieldConsultant" @input=${this.filterSelectOptionsConsultant}></mwc-textfield>
            <mwc-select 
            outlined
            label="Berater"
            id="consultantId" style="width:30%;" @change=${this.changeConsultValue}>    
              ${this.filterConsultants.map((consultant: Consultant) => {
                return html`
                  <mwc-list-item
                    value="${consultant.consultantID}"
                    style="width:fit-content;">
                    ${consultant.consultantID} - ${consultant.name}
                  </mwc-list-item>
                `;                
              })}
            </mwc-select>
          </label>
          <br />
          <label for="clientId">
            <span><b>Mandant:</b></span>
            <mwc-textfield 
                outlined
                label="Suche Mandanten.." TextFieldType="search" id="searchfieldClient" @input=${this.filterSelectOptionsClient}></mwc-textfield>
            <mwc-select
              outlined
              label="Mandant"
              id="clientId" style="width:30%;" @change=${this.changeClientValue}>
              ${this.filterClients.map((client: Client) => {
                return html`
                  <mwc-list-item
                    value="${client.clientID}"
                    style="width:fit-content;">
                    ${client.clientID} - ${client.name}
                  </mwc-list-item>
                `;
              })}
            </mwc-select>
          </label>
          <br />
        </form>
      </div>
      <div id="drop-area" 
        @dragenter=${this.preventDefaults, this.highlight} 
        @dragover=${this.preventDefaults, this.highlight} 
        @dragleave=${this.preventDefaults, this.unhighlight} 
        @dragend=${this.preventDefaults, this.unhighlight} 
        @drop=${this.preventDefaults, this.unhighlight, this.handleDrop}>
        <form class="my-form" @submit="${(e: Event) => e.preventDefault()}">
          <p>
            W&auml;hlen Sie PDF-Dokument(e) mit dem Dateidialog aus oder indem
            Sie Dokumente per Drag & Drop auf den gestrichelten Bereich ziehen
          </p>
          <input
            type="file"
            id="fileElem"
            multiple
            accept=".pdf,application/pdf"
            @change="${this.handleFiles}" />
          <label class="button" for="fileElem">Datei(en) ausw&auml;hlen</label>
          <button
            class="button"
            id="uploadButton"
            @click="${this.uploadFiles}"
            style="visibility: hidden">
            Dokument(e) hochladen
          </button>
        </form>
        <progress id="progress-bar" max="100" value="0"></progress>
      </div>
    `);
    return template;
  }

  /**
   * Uploading process for the files
   * @returns 
   */
  uploadFiles() {
    let clientId = (
      this.shadowRoot?.getElementById("clientId") as HTMLInputElement
    ).value;
    let consultantId = (
      this.shadowRoot?.getElementById("consultantId") as HTMLInputElement
    ).value;
    this.dialogInit();
    //Shows dialog if consultant or client are not selected
    if (consultantId === "") {
        this.dialogText.innerHTML="Tragen Sie bitte eine Beraternummer ein";
        this.dialog!.appendChild(this.dialogText);
        this.dialog!.appendChild(this.footerButtonOk);
        this.dialog!.open = true;
        return;
    } else if (clientId === "") {
        this.dialogText.innerHTML="Tragen Sie bitte eine Mandantennummer ein";
        this.dialog!.appendChild(this.dialogText);
        this.dialog!.appendChild(this.footerButtonOk);
        this.dialog!.open = true;
      return;
    } 
    if(this.consultantClients.find((consultantClient: ConsultantClient) => 
          consultantClient.clientID == BigInt(clientId) 
          && consultantClient.consultantID == BigInt(consultantId))){
            
        this.initializeProgress(this.files.length);
        this.getAccessToken(true)
        .then((accessToken: string) => {
            let file: File;
            let url: string;
            let filename: string;
            for (let i = 0, numFiles = this.files.length; i < numFiles; i++) {
            file = this.files[i];
            filename = "B"+consultantId+"_M"+clientId+"_"+file.name;
            url =
                "https://" +
                this.upload_bucket +
                ".storage.googleapis.com/" +
                encodeURIComponent(crypto.randomUUID());
            
            //Opening xhr request
            const xhr = new XMLHttpRequest();
            var formData = new FormData();
            xhr.open("PUT", url, true);
            xhr.setRequestHeader("Authorization", "Bearer " + accessToken);
            xhr.setRequestHeader("Content-Type", "application/pdf");
            xhr.setRequestHeader("X-Goog-Meta-originalFileName", filename || "");
            xhr.setRequestHeader("X-Goog-Meta-databaseName", this.database?.databaseName || "");
            xhr.setRequestHeader("X-Goog-Meta-processorName", this.processorName || "");
            xhr.setRequestHeader("X-Goog-Meta-processorType", this.processorType || "");
            xhr.setRequestHeader("X-Goog-Meta-userID", this.userID);
            xhr.setRequestHeader("X-Goog-Meta-clientID", clientId);
            xhr.setRequestHeader("X-Goog-Meta-consultantID", consultantId);
            // Update progress (can be used to show progress indicator)
            xhr.upload.addEventListener("progress", (e) => {
                this.updateProgress(i, (e.loaded * 100.0) / e.total || 100);
            });
            xhr.addEventListener("readystatechange", () => {                
                if (xhr.readyState == 4 && xhr.status == 200) {
                this.updateProgress(i, 100);
                
                if(this.dialog){
                    this.dialogInit();
                    this.dialogText.innerHTML = "Dokument ist hochgeladen.";
                    this.dialog.appendChild(this.dialogText);
                    this.dialog.appendChild(this.footerButtonOk);
                    this.dialog.classList.add("dialog-style");
                    this.dialog.addEventListener("closed", () =>{
                        const clientSearchText = (this.renderRoot.querySelector("#searchfieldClient") as HTMLInputElement);
                        const consultantSearchText = (this.renderRoot.querySelector("#searchfieldConsultant") as HTMLInputElement);
                        
                          if(this.dialog!.open == false){
                              if(this.dialog!.children.length > 1){
                                this.dialog!.removeChild(this.dialog!.children[1]);
                              }
                              if(this.progressBar && this.progressBar.value == 100){
                                  this.progressBar.value = 0;
                              }
                              if (this.uploadButton){
                                  const buttonFileElem = this.renderRoot.querySelector('#fileElem') as HTMLInputElement;
                                  buttonFileElem.value = "";
                                  this.uploadButton.style.visibility = "hidden";                                  
                              } 
                              this.dialog!.innerHTML="";
                              clientSearchText.value = "";
                              consultantSearchText.value = "";
                          }
                      });
                    this.dialog.open= true;  
                }
                //Values of consultant, client, filterclients and filterconsultants dropdown will reset
                const selectionListConsultant = (this.renderRoot.querySelector("#consultantId") as HTMLInputElement);
                const selectionListClient = (this.renderRoot.querySelector("#clientId") as HTMLInputElement);
                selectionListConsultant.value = "";
                selectionListClient.value = "";
                this.filterClients= this.clients;
                this.filterConsultants = this.consultants;
                console.log(this.filterClients);
                //Error handling if Upload did not work
                } else if (xhr.readyState == 4 && xhr.status != 200) {
                    this.dialogInit();
                    this.dialogText.innerHTML = "Hochladen fehlgeschlagen.";
                    this.dialog!.appendChild(this.dialogText);
                    this.dialog!.appendChild(this.footerButtonOk);
                    this.dialog!.classList.add("dialog-style");
                    this.dialog!.open= true;
                }
            });
            formData.append("file", file);
            //sending xhr request
            xhr.send(formData);
            }
        })
        .catch((error: any) => console.log(error)); 
    //In case that Client doesnt has the selected Consultant
    } else {  
        this.dialogInit();
        this.dialogText.innerHTML = "Der ausgewählte Mandant ist nicht dem Berater zugeordnet!";
        this.dialog!.appendChild(this.dialogText);
        this.dialog!.appendChild(this.footerButtonOk);
        this.dialog!.classList.add("dialog-style");
        this.dialog!.open= true;
    }
  }
  
  /**
   * Event method to handle dropped documents
   * @param {DragEvent} e 
   */
  handleDrop(e: DragEvent) {
    this.preventDefaults(e);
    const dt = e.dataTransfer;
    this.uploadButton = this.shadowRoot?.getElementById(
      "uploadButton"
    ) as HTMLButtonElement;
    if (dt) {
      const files = dt.files;
      if (files.length > 0) {
        this.handleFiles(e);
      }
      this.uploadButton.innerHTML = `${this.files.length} `;
      //Check if the amount of files is one or more
      if (this.files.length === 1) {
        this.uploadButton.innerHTML += "Dokument hochladen";
      } else {
        this.uploadButton.innerHTML += "Dokumente hochladen";
      }
      this.uploadButton.style.visibility = "visible";
    }
    //Solves unhighlight issue
    this.unhighlight(e);        
  }

  /**
   * Initialize progress of progressbar
   * @param numFiles 
   */
  initializeProgress(numFiles: number) {
    if (this.progressBar) {
      this.progressBar.value = 0;
    }
    this.uploadProgress = Array(numFiles).fill(0);
  }

  /**
   * Updates progressbar value based on uploaded documents
   * @param fileNumber 
   * @param percent 
   */
  updateProgress(fileNumber: number, percent: number) {
    this.uploadProgress[fileNumber] = percent;
    let total =
      this.uploadProgress.reduce((total, curr) => total + curr, 0) /
      this.uploadProgress.length;
      this.progressBar = this.shadowRoot?.getElementById(
      "progress-bar"
    ) as HTMLProgressElement;
    this.progressBar.value = total;
  }

  /**
   * Prevents events to execute at false time
   * @param e 
   */
  preventDefaults(e:Event){
    e.preventDefault();
    e.stopPropagation();
  }

  /**
   * Event to highlight droparea
   * @param e 
   */
  highlight(e:Event){
    e.preventDefault();
    this.dropArea.classList.add('highlight');
  }

  /**
   * Event to unhighlight drop area
   * @param e 
   */
  unhighlight(e:Event){
    e.preventDefault();
    this.dropArea.classList.remove('highlight');
  }
  
  /**
   * Event to handle files. The handle of the files depends on uploaded per Button click or per drag and drop
   * @param e 
   */
  handleFiles(e:DragEvent | any) {        //Only for Button "any"
    this.preventDefaults(e);
    if(e instanceof DragEvent)this.files = [...(e.dataTransfer!.files || [])]; //If Files are added per drag & drop
    else{
        this.files = [...((e.target as HTMLInputElement).files || [])]; //If files are added per button
    }
    this.progressBar = document.getElementById("progress-bar") as HTMLProgressElement;
    //Set value to progress bar based on files length
    if (this.progressBar) {
        this.progressBar.value = this.files.length;
    }
    this.uploadButton = this.shadowRoot?.getElementById("uploadButton") as HTMLButtonElement;
    if (this.uploadButton) {
        this.uploadButton.innerHTML = `${this.files.length} `;
      if (this.files.length === 1) {
        this.uploadButton.innerHTML += "Dokument hochladen";
      } else {
        this.uploadButton.innerHTML += "Dokumente hochladen";
      }
      this.uploadButton.style.visibility = "visible";
    }
  }

  /**
   * Assign consultants which consultant id or consultant name matches with the input of the search field
   */
  assignConsultantsBySearch(){
    //Search field for consultant
    const searchInputConsultant = (this.renderRoot.querySelector("#searchfieldConsultant") as HTMLInputElement).value;
    
    this.filterConsultants = this.consultants.filter((consultant: Consultant) => 
            consultant.name.toLowerCase().indexOf(searchInputConsultant.toLowerCase()) !== -1 
            || consultant.consultantID.toString().indexOf(searchInputConsultant) !== -1);
  }

  /**
   * Assign clients which client id or client name matches with the input of the search field
   */
  assignClientsBySearch(){
    //Search field for client
    const searchInputClient = (this.renderRoot.querySelector("#searchfieldClient") as HTMLInputElement).value;

    this.filterClients = this.filterClients.filter((client: Client) => 
            client.name.toLowerCase().indexOf(searchInputClient.toLowerCase()) !== -1 
            || client.clientID.toString().indexOf(searchInputClient) !== -1); 

  }

  /**
   * Filter consultant list based on the searchtextfield for consultants
   * @param e 
   */
  filterSelectOptionsConsultant = async (e : CustomEvent) => {
    this.preventDefaults(e);
    
    //Dropdown items
    const selectedConsultant = (this.renderRoot.querySelector("#consultantId") as HTMLInputElement);
    const selectedClient = (this.renderRoot.querySelector("#clientId") as HTMLInputElement);
    //Dropdown values
    let clientSelectValue= selectedClient.value;
    let consultantSelectValue = selectedConsultant.value;
    let currentCase= "";
    this.assignConsultantsBySearch();
    //If client and consultant are both not selected
    if (!(clientSelectValue && consultantSelectValue)){
      this.assignClientsBySearch();
        currentCase = 'Case 1';
    //If client and consultant are selected
    }else {
      //Resets consultant value in dropdown
      consultantSelectValue ="";
      if(clientSelectValue){
          const consultantIDsForClient = this.consultantClients
                .filter((consultantClient) => BigInt(consultantClient.clientID) === BigInt(clientSelectValue))
                .map((consultantClient) => consultantClient.consultantID);
          this.filterConsultants = this.filterConsultants.filter((consultant) => consultantIDsForClient.includes(consultant.consultantID));         
          this.assignClientsBySearch();   
      }
      currentCase = 'Case 2';
    }
    this.requestUpdate();
    console.log("Filter consultants: ",currentCase);
  };

  /**
   * Filter client list based on the searchtextfield for clients
   * @param _ 
   */
  filterSelectOptionsClient = async (_ : CustomEvent) => {
    console.log("FilterSelectOptionsClient got triggered");
    //Dropdowns
    const selectedConsultant = (this.renderRoot.querySelector("#consultantId") as HTMLInputElement);
    const selectedClient = (this.renderRoot.querySelector("#clientId") as HTMLInputElement);
    //Dropdown values
    let clientSelectValue= selectedClient.value;
    let consultantSelectValue = selectedConsultant.value;
    let currentCase= "";
    this.assignClientsBySearch();
        //If consultant and client are both not selected
        if (!(consultantSelectValue && clientSelectValue)){
            this.filterClients = this.clients.filter((client: Client) => 
            this.consultantClients.find((consultantClient: ConsultantClient) => 
                consultantClient.clientID == client.clientID 
                && BigInt(selectedConsultant.value) == consultantClient.consultantID));
            this.assignClientsBySearch();
            currentCase = 'Case 1';
           //If client and consultant are selected 
          }else{
            clientSelectValue ="";
            //If consultant value is selected
            if(consultantSelectValue){
              /**
               * Client ids for consultants
               */
              const clientIDsForConsultant = this.consultantClients
                      .filter((consultantClient) => BigInt(consultantClient.consultantID) === BigInt(consultantSelectValue))
                      .map((consultantClient) => consultantClient.clientID);
              this.filterClients = this.filterClients.filter((client) => clientIDsForConsultant.includes(client.clientID));
              this.assignConsultantsBySearch();
              currentCase = 'Case 2';
            } 
            
    }
    this.requestUpdate();
    console.log("Filter clients: ",currentCase);
  };

  /**
   * Change the value list of the clients based on the consultant-client connection
   * @param _ 
   */
  changeConsultValue = async (_ : CustomEvent) => {
    console.log("changeConsultantValue got triggered")
    const selectedConsultant = (this.renderRoot.querySelector("#consultantId") as HTMLInputElement);
    const selectedClient = (this.renderRoot.querySelector("#clientId") as HTMLInputElement);
    const initialSelectedClient = selectedClient.value;
    
    if (selectedConsultant.value && selectedClient.value) {
      this.filterConsultants = this.consultants.filter((consultant) =>
          this.consultantClients.find((cclients) =>
              BigInt(cclients.clientID) === BigInt(selectedClient.value) &&
              BigInt(cclients.consultantID) === BigInt(consultant.consultantID)
          )
      );    
      console.log(this.filterConsultants);
      //If consultant is selected but no client
    } else if(selectedConsultant.value && !(selectedClient.value)){
        this.filterClients = this.clients.filter((client) => 
          this.consultantClients.find((cclients) => 
                BigInt(cclients.consultantID) == BigInt(selectedConsultant.value))?.clientID == client.clientID);
        console.log(this.filterClients);
    }       
    
    if (selectedClient.value !== '') {
      (this.renderRoot.querySelector("#clientId") as HTMLInputElement).value = initialSelectedClient;
    }
    this.requestUpdate();
  };

  /**
   * Change the value list of the consultants based on the consultant-client connection
   * @param _ 
   */
  changeClientValue = async (_ : CustomEvent) => {
    console.log("changeClientValue got triggered");
    const selectedConsultant = (this.renderRoot.querySelector("#consultantId") as HTMLInputElement);
    const selectedClient = (this.renderRoot.querySelector("#clientId") as HTMLInputElement);
    
    const initialSelectedConsultant = selectedConsultant.value;
    
    if (selectedConsultant.value && selectedClient.value) {
      this.filterClients = this.clients.filter((client) =>
          this.consultantClients.find((cclients) =>
              BigInt(cclients.consultantID) === BigInt(selectedConsultant.value) &&
              BigInt(cclients.clientID) === BigInt(client.clientID)
          )
      );
      console.log(this.filterClients);
      console.log(this.filterConsultants);
      //If no consultant is selected but client is selected
    	} else if(!selectedConsultant.value && selectedClient.value){
      this.filterConsultants = this.consultants.filter((consultant) => 
          this.consultantClients.find((cconsultants) => BigInt(cconsultants.clientID) == BigInt(selectedClient.value))?.consultantID == consultant.consultantID);      
      console.log(this.filterConsultants);
      
    }
    if (selectedConsultant.value !== '') {
      (this.renderRoot.querySelector("#consultantId") as HTMLInputElement).value = initialSelectedConsultant;
    }
    this.requestUpdate();
  };

  /**
   * Initialize dialog
   */
  dialogInit(){
    this.dialog!.innerHTML ="";
    this.footerButtonOk.innerHTML ="";
    this.footerButtonOk.setAttribute("slot", "primaryAction");
    this.footerButtonOk.setAttribute("dialogAction", "ok");
    this.footerButtonOk.innerText = "Ok";
    this.footerButtonOk.classList.add("raised");
    this.dialogText.style.padding = "0px 20%";
    this.dialog!.setAttribute('style', 'border-color: #000000; border-width: 1px; color: #000000; font-size: 1.1em;');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "hitl-upload": HitlUpload;
  }
}
